import { forwardRef, Fragment, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { getFirstDayOfWeek } from '@sas/nova-core/i18n/datetime';
import { getLogger } from '@sas/nova-core/logging';
import cx from 'classnames';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useMounted } from '../../../hooks/useMounted';
import { useRefArray } from '../../../hooks/useRefArray';
import { useScroll } from '../../../hooks/useScroll';
import { useIsRTL } from '../../../locale';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../../locale/formatting/useFormatLocale';
import { isArSaLocale, isThaiLocale } from '../../../locale/localeUtils';
import { useDesignTokens } from '../../../theme';
import { markEvent } from '../../../util/events';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { Button, ButtonVariant } from '../../Button';
import { useDefaultDateTimeFormat } from '../../DatePicker';
import { convertGregorianYearToThaiYear, convertThaiYearToGregorianYear, isSameDay } from '../../DatePicker/DateUtil';
import { InvisibleText } from '../../InvisibleText';
import { GlobalSettingsKeys } from '../../Settings/GlobalSettingsKeys';
// avoid cycle dependencies
import { useGetGlobalSetting } from '../../Settings/GlobalSettingsProvider';
import { createDisabledDayFilter, isAfterDate, isPastDate } from '../DisabledDayFilters';
import { getCalendarRows, getCalendarWeekNumber } from '../GregorianCalendarFacts';
import { CalendarLegend } from './CalendarLegend';
import useSelectedAriaDescribedby from './useSelectedAriaDescribedby';
import bundle from '../NovaComponents-Calendar-gui-icu.properties';
import cn from '../Calendar.css.json';
import '../Calendar.css';
import { DayCell } from './DayCell';
import { MonthCalendarHeader } from './MonthCalendarHeader';
import { MultipleMonthCalendarHeader } from './MultipleMonthCalendarHeader';
import { getCalendarDesignTokens } from './types';
import { WeekdayHeaderCells } from './WeekDayHeaderCells';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useCalendarId = createUseHtmlId('calendar');
const useCalendarSelectionId = createUseHtmlId('Calendar-selection');
const useCalendarSelectionStateId = createUseHtmlId('Calendar-selection-state');
const useCalendarIntervalStartId = createUseHtmlId('Calendar-interval-start');
const useCalendarIntervalEndId = createUseHtmlId('Calendar-interval-end');
const useCalendarSelectId = createUseHtmlId('Calendar-select');
const useTextLocalizer = createUseTextLocalizer(bundle);
const logger = getLogger('Calendar'); // eslint-disable-line @sas/i18n/jsx-to-localize

const defaultMinDate = new Date(1582, 0, 1);
const defaultMaxDate = new Date(9999, 11, 31);
let minDate;
let maxDate;

// Define the type for an array of Date arrays

export const BaseCalendar = /*#__PURE__*/forwardRef(function BaseCalendar({
  onChange,
  intervalSelection,
  ...props
}, ref) {
  const dayFormat = useDefaultDateTimeFormat({
    type: 'dayOnly'
  });
  const monthFormat = useDefaultDateTimeFormat({
    type: 'monthOnly'
  });
  const isCurrentMonth = useCallback((day, rowIndex) => {
    if (parseInt(dayFormat(day)) > 7 && rowIndex === 0) return false;
    if (parseInt(dayFormat(day)) <= 7 && rowIndex >= 4) return false;
    return true;
  }, [dayFormat]);
  const getDayNumber = dayName => {
    const daysOfWeek = {
      sun: 0,
      mon: 1,
      tue: 2,
      wed: 3,
      thu: 4,
      fri: 5,
      sat: 6
    };
    const normalizedDay = dayName.toLowerCase();
    return daysOfWeek[normalizedDay] !== undefined ? daysOfWeek[normalizedDay] : 0; // default to Sunday
  };

  const localize = useTextLocalizer();
  const locale = useFormatLocale();
  const firstDayOfWeek = props.firstDayOfWeek && props.firstDayOfWeek >= 0 && props.firstDayOfWeek <= 6 ? props.firstDayOfWeek : getDayNumber(getFirstDayOfWeek(locale));
  const contentRef = useRef(null);
  const {
    scrollTo
  } = useScroll(contentRef, 'vertical');
  const today = useMemo(() => {
    return new Date();
  }, []);
  const dayOfWeekIds = useCalendarId(7);
  const isRTL = useIsRTL();
  const mountedRef = useMounted();
  const singleSelection = props.singleSelection ?? true;
  const [prevDateRangeDate, setPrevDateRangeDate] = useState(props.dateRange);
  const [dateRangeDates, setDateRangeDates] = useState(props.dateRange ? props.dateRange : []);
  const [selectedDates, setSelectedDates] = useState([]);
  const nonWorkDays = props.nonWorkDays ?? [0, 6];
  const monthsCount = props.months && props.months > 0 ? props.months : 1;
  // displayedMonths and monthsRendered are used for multiple month calendar
  const [displayedMonths, setDisplayedMonths] = useState([]);
  const monthsRendered = useRef([]);
  // this is used to prevent month header from resetting when the user clicks on a day cell
  const [dayCellPress, setDayCellPress] = useState(false);
  minDate = props.minDate && isAfterDate(props.minDate, {
    today: defaultMinDate
  }) && (isPastDate(props.minDate, {
    today: defaultMaxDate
  }) || isSameDay(props.minDate, defaultMaxDate)) ? new Date(props.minDate) : new Date(defaultMinDate);
  minDate.setHours(0, 0, 0, 0);
  maxDate = props.maxDate && (isAfterDate(props.maxDate, {
    today: defaultMinDate
  }) || isSameDay(props.maxDate, defaultMinDate)) && isPastDate(props.maxDate, {
    today: defaultMaxDate
  }) ? new Date(props.maxDate) : new Date(defaultMaxDate);
  maxDate.setHours(23, 59, 59);
  if (props.minDate && props.maxDate && props.minDate.getTime() > props.maxDate.getTime()) {
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    logger.warn('minDate > MaxDate -> dates switched');
    const tempMaxDate = new Date(props.minDate.getTime());
    const tempMinDate = new Date(props.maxDate.getTime());
    minDate = new Date(tempMinDate.getTime());
    maxDate = new Date(tempMaxDate.getTime());
  }
  const firstValidDate = useMemo(() => {
    const dateVal = props.value ?? new Date();
    if (props.minDate && dateVal.getTime() < props.minDate.getTime()) {
      return new Date(props.minDate);
    } else if (props.maxDate && dateVal.getTime() > props.maxDate.getTime()) {
      return new Date(props.openOnMinDate && props.minDate ? props.minDate : props.maxDate);
    }
    return dateVal;
  }, [props.minDate, props.maxDate, props.openOnMinDate, props.value]);

  // useEffect will select first valid date and updates datePicker's input field.
  useEffect(() => {
    if (props.value && (props.minDate || props.maxDate) && !isSameDay(props.value, firstValidDate)) {
      onChange?.({
        value: firstValidDate
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isBeforeMinDate = day => isPastDate(day, {
    today: minDate
  });
  const isAfterMaxDate = day => isAfterDate(day, {
    today: maxDate
  });
  const isNonWorkDay = day => {
    if (!props.showNonWorkDays) {
      return false;
    }
    return nonWorkDays.some(nonWorkDay => day.getDay() === nonWorkDay);
  };
  const isSpecialDay = day => {
    if (!props.specialDates) {
      return false;
    }
    const isSpecialDate = specialDate => {
      if (specialDate.startDate && specialDate.endDate) {
        const startDate = specialDate.startDate;
        const endDate = specialDate.endDate;
        if (startDate && day.getTime() >= startDate.getTime() && endDate && day.getTime() <= endDate.getTime()) {
          return true;
        }
      } else {
        const startDate = specialDate.startDate;
        return startDate && isSameDay(startDate, day);
      }
      return false;
    };
    return props.specialDates.dateRange.some(specialDate => isSpecialDate(specialDate));
  };
  const disabledDayFilter = useMemo(() => {
    if (!props.disabledDays) {
      return undefined;
    } else if (typeof props.disabledDays === 'function') {
      return props.disabledDays;
    } else {
      return createDisabledDayFilter(props.disabledDays);
    }
  }, [props.disabledDays]);
  const isDisabledDay = useCallback(day => {
    let returnVal = false;
    if (isBeforeMinDate(day) || isAfterMaxDate(day)) returnVal = true;
    if (!returnVal && props.disabledDates) {
      returnVal = props.disabledDates.some(disabledDate => isSameDay(disabledDate, day));
    }
    if (!returnVal && props.enabledDates) {
      returnVal = props.enabledDates.every(enabledDate => !isSameDay(enabledDate, day));
    }
    if (!returnVal && disabledDayFilter) {
      returnVal = disabledDayFilter(day, {
        today
      });
    }
    return returnVal;
  }, [disabledDayFilter, props.disabledDates, props.enabledDates, today]);
  const isValidSingleSelection = date => {
    if (!isBeforeMinDate(date) && !isAfterMaxDate(date)) {
      if (mountedRef.current && singleSelection && !intervalSelection && selectedDates.length > 0) return true;
    }
    return false;
  };
  const [state, setState] = useState(props.value ?? new Date());
  const [forceDayCellFocus, setForceDayCellFocus] = useState(false);
  const [focusState, dispatchFocusAction] = useReducer((prevState, action) => {
    switch (action.type) {
      case 'step':
        {
          const nextDate = new Date(prevState);
          nextDate.setHours(0, 0, 0, 0);
          let delta = 0;
          if (action.direction === 'up') {
            delta = -7;
          } else if (action.direction === 'down') {
            delta = 7;
          } else if (action.direction === 'left') {
            delta = isRTL ? 1 : -1;
          } else if (action.direction === 'right') {
            delta = isRTL ? -1 : 1;
          } else if (action.direction === 'startOfWeek') {
            delta = nextDate.getDay() * -1;
          } else if (action.direction === 'endOfWeek') {
            delta = 6 - nextDate.getDay();
          }
          nextDate.setDate(nextDate.getDate() + delta);
          if (nextDate.getTime() < minDate.getTime() || nextDate.getTime() > maxDate.getTime()) {
            return new Date(prevState);
          }
          if (nextDate.getMonth() !== prevState.getMonth()) {
            const dte_1 = new Date(nextDate.getFullYear(), nextDate.getMonth(), state.getDate());
            if (dte_1.getMonth() !== nextDate.getMonth()) {
              while (dte_1.getMonth() !== nextDate.getMonth()) dte_1.setDate(dte_1.getDate() - 1);
            }
            if (isValidSingleSelection(dte_1)) {
              setTimeout(() => {
                onChange?.({
                  value: dte_1
                });
              }, 0);
            }
          }
          return nextDate;
        }
      case 'monthStep':
        {
          const nextDate = new Date(prevState);
          nextDate.setHours(0, 0, 0, 0);
          if (action.direction === 'next') {
            // See if current date is greater than number of days in new month, if so, set date to last date of new month
            const testDate = new Date(nextDate.getFullYear(), nextDate.getMonth() + 2, 0).getDate();
            if (nextDate.getDate() > testDate) nextDate.setDate(testDate);
            nextDate.setMonth(nextDate.getMonth() + 1);
          } else if (action.direction === 'previous') {
            // See if current date is greater than number of days in new month, if so, set date to last date of new month
            const testDate = new Date(nextDate.getFullYear(), nextDate.getMonth(), 0).getDate();
            if (nextDate.getDate() > testDate) nextDate.setDate(testDate);
            nextDate.setMonth(nextDate.getMonth() - 1);
          }
          const lastDate = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();
          const newDate = new Date(nextDate);
          if (isDisabledDay(newDate)) {
            // First go backwards to find an enabled date
            while (isDisabledDay(newDate) && newDate.getDate() !== 1) {
              newDate.setDate(newDate.getDate() - 1);
              if (!isDisabledDay(newDate)) nextDate.setDate(newDate.getDate());
            }
            if (isDisabledDay(newDate)) {
              // If no enabled date going backwards, now go forward to find an enabled date
              while (isDisabledDay(newDate) && newDate.getDate() < lastDate) {
                newDate.setDate(newDate.getDate() + 1);
                if (!isDisabledDay(newDate)) nextDate.setDate(newDate.getDate());
              }
            }
          }
          if (nextDate.getTime() < minDate.getTime() || nextDate.getTime() > maxDate.getTime()) {
            return new Date(prevState);
          }
          const dte_2 = new Date(nextDate.getFullYear(), nextDate.getMonth(), newDate.getDate());
          if (dte_2.getMonth() !== nextDate.getMonth()) {
            while (dte_2.getMonth() !== nextDate.getMonth()) dte_2.setDate(dte_2.getDate() - 1);
          }
          if (isValidSingleSelection(dte_2)) {
            setTimeout(() => {
              onChange?.({
                value: dte_2
              });
            }, 0);
          }
          return nextDate;
        }
      case 'yearStep':
        {
          const nextDate = new Date(prevState);
          nextDate.setHours(0, 0, 0, 0);
          if (action.direction === 'next') {
            nextDate.setFullYear(nextDate.getFullYear() + 1);
          } else if (action.direction === 'previous') {
            nextDate.setFullYear(nextDate.getFullYear() - 1);
          }
          if (nextDate.getTime() < minDate.getTime() || nextDate.getTime() > maxDate.getTime()) {
            return new Date(prevState);
          }
          const dte_3 = new Date(nextDate.getFullYear(), nextDate.getMonth(), state.getDate());
          if (dte_3.getMonth() !== nextDate.getMonth()) {
            while (dte_3.getMonth() !== nextDate.getMonth()) dte_3.setDate(dte_3.getDate() - 1);
          }
          if (isValidSingleSelection(dte_3)) {
            setTimeout(() => {
              onChange?.({
                value: dte_3
              });
            }, 0);
          }
          return nextDate;
        }
      case 'decadeStep':
        {
          const nextDate = new Date(prevState);
          nextDate.setHours(0, 0, 0, 0);
          if (action.direction === 'next') {
            nextDate.setFullYear(nextDate.getFullYear() + 10);
          } else if (action.direction === 'previous') {
            nextDate.setFullYear(nextDate.getFullYear() - 10);
          }
          if (nextDate.getTime() < minDate.getTime() || nextDate.getTime() > maxDate.getTime()) {
            return new Date(prevState);
          }
          const dte_4 = new Date(nextDate.getFullYear(), nextDate.getMonth(), state.getDate());
          if (dte_4.getMonth() !== nextDate.getMonth()) {
            while (dte_4.getMonth() !== nextDate.getMonth()) dte_4.setDate(dte_4.getDate() - 1);
          }
          if (isValidSingleSelection(dte_4)) {
            setTimeout(() => {
              onChange?.({
                value: dte_4
              });
            }, 0);
          }
          return nextDate;
        }
      case 'monthSelect':
        // See if prevState date greater than number of days in new month, if so, set date to last date of new month
        let year = action.value.year;
        if (isNaN(year)) year = prevState.getFullYear();
        if (isThaiLocale(locale)) {
          if (monthsCount === 1 || year > 999) {
            // Thai year should be at least 4 digits if multi month calendar
            // to convert to Gregorian year
            year = convertThaiYearToGregorianYear(year);
          }
        }
        const lastDate = new Date(year, action.value.month + 1, 0).getDate();
        let newDate;
        if (prevState.getDate() > lastDate) {
          newDate = new Date(year, action.value.month, lastDate);
        } else newDate = new Date(year, action.value.month, prevState.getDate());
        if (year < 100) {
          // JavaScript's Date object interprets years less than 100 as being in the 20th century.
          // For example, if year is 2, it will be interpreted as 1902.  This is not what we want.
          // We want it to be interpreted as 0002. So we need to set the year to the desired value.
          newDate.setFullYear(year);
        }
        const tmpDate = new Date(newDate);
        if (isDisabledDay(tmpDate)) {
          // First go backwards to find an enabled date
          while (isDisabledDay(tmpDate) && tmpDate.getDate() !== 1) {
            tmpDate.setDate(tmpDate.getDate() - 1);
            if (!isDisabledDay(tmpDate)) newDate.setDate(tmpDate.getDate());
          }
          if (isDisabledDay(tmpDate)) {
            // If no enabled date going backwards, now go forward to find an enabled date
            while (isDisabledDay(tmpDate) && tmpDate.getDate() < lastDate) {
              tmpDate.setDate(tmpDate.getDate() + 1);
              if (!isDisabledDay(tmpDate)) newDate.setDate(tmpDate.getDate());
            }
          }
        }
        const dte_5 = new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
        if (dte_5.getMonth() !== newDate.getMonth()) {
          while (dte_5.getMonth() !== newDate.getMonth()) dte_5.setDate(dte_5.getDate() - 1);
        }
        if (isValidSingleSelection(dte_5)) {
          if (isArSaLocale(locale) && typeof action.value.day === 'number') dte_5.setDate(action.value.day);
          // SetTimeout was added to avoid a bad state on first render, when the user selects a different month --> see NOVACOMMONS-13238
          setTimeout(() => {
            onChange?.({
              value: dte_5
            });
          }, 0);
        }
        if (isArSaLocale(locale) && typeof action.value.day === 'number') newDate.setDate(action.value.day);
        return newDate;
      case 'selection':
        return action.value;
      default:
        return prevState;
    }
  }, firstValidDate);
  const calendar = useMemo(() => getCalendarRows(focusState.getMonth(), focusState.getFullYear(), locale, firstDayOfWeek), [focusState, locale, firstDayOfWeek]);
  const getFormatedCalendar = useCallback((calndr, date) => {
    if (!isArSaLocale(locale)) return calndr;
    const dayOneIndex = calndr.findIndex(row => row.find(day => {
      const isItem = day.getDate() === date.getDate() && day.getMonth() === date.getMonth();
      return isItem;
    }));
    let startIndex = 0;
    const formatedDate = parseInt(dayFormat(date));
    if (formatedDate >= 1 && formatedDate <= 7) {
      const temp = calndr[dayOneIndex].find(day => parseInt(dayFormat(day)) === 1);
      if (temp) {
        startIndex = dayOneIndex;
      } else {
        startIndex = dayOneIndex - 1;
      }
    } else {
      for (let i = dayOneIndex - 1; i >= dayOneIndex - 5; i--) {
        const temp = calndr[i].find(day => parseInt(dayFormat(day)) === 1);
        if (temp) {
          startIndex = i;
          break;
        }
      }
    }
    let endIndex = startIndex + 5;
    for (let i = endIndex; i >= endIndex - 1; i--) {
      const temp = calndr[i]?.findIndex(day => parseInt(dayFormat(day)) === 1);
      if (temp >= 0) {
        endIndex = temp === 0 ? i - 1 : i;
        break;
      }
    }
    const formatedCal = calndr.filter((_row, index) => index >= startIndex && index <= endIndex);
    return formatedCal;
  }, [dayFormat, locale]);
  const formatedCalendar = useMemo(() => getFormatedCalendar(calendar, focusState), [focusState, calendar, getFormatedCalendar]);
  const weekNumberIds = useCalendarId(calendar.length);
  const {
    refs: dayCells,
    createSetRef: createSetDayCell
  } = useRefArray();
  const getDayCell = useCallback(day => {
    for (let index = 0; index < formatedCalendar.length * 7; ++index) {
      const currentDay = formatedCalendar[Math.floor(index / 7)][index % 7];
      if (isSameDay(currentDay, day)) {
        if (monthsCount > 1) {
          let divCellIndex = 0;
          dayCells.forEach((item, elementIndex) => {
            if (item && item.ariaLabel === day.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })) {
              divCellIndex = elementIndex;
            }
          });
          return dayCells[divCellIndex];
        } else {
          return dayCells[index];
        }
      }
    }
  }, [formatedCalendar, dayCells, monthsCount]);
  const {
    refs: divRefs,
    createSetRef: createSetDivRef
  } = useRefArray();
  const {
    refs: dayCellContainers,
    createSetRef: createSetDayCellContainer
  } = useRefArray();
  const refDayCellContainer = useRef(null);
  const headerRef = useRef(null);
  const [hasDayCellFocus, setHasDayCellFocus] = useState(false);
  // focus management for the day cell grid
  {
    const refPrevFocusState = useRef(focusState);
    useEffect(() => {
      if (!forceDayCellFocus && (refPrevFocusState.current === focusState || !hasDayCellFocus)) {
        return;
      }
      refPrevFocusState.current = focusState;
      getDayCell(focusState)?.focus();
      setForceDayCellFocus(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusState, formatedCalendar, hasDayCellFocus, ref, forceDayCellFocus, dayCellContainers]);
  }
  useEffect(() => {
    // when new months are rendered, we need to set the focus to the day cell that was focused before the month change
    if (monthsCount > 1) {
      if (!isArSaLocale(locale)) {
        getDayCell(focusState)?.focus();
      }
    }
  }, [displayedMonths, focusState, getDayCell, locale, monthsCount]);
  const weekNumber = useMemo(() => props.showWeekNumbers ? getCalendarWeekNumber(focusState.getFullYear(), focusState.getMonth()) : NaN, [focusState, props.showWeekNumbers]);
  const getWeekNum = (week, monthIndex, year) => {
    const d = 1 + (week - 1) * 7; // 1st of January + 7 days for each week
    const date = new Date(year, 0, d);
    const first = date.getDate() - date.getDay(); // First day of the week
    const last = first + 6; // last day is the first day + 6
    const lastDate = new Date(date.setDate(last));
    if (monthIndex === 11 && lastDate.getMonth() === 0) return 1;
    return week;
  };
  const [focusVisible, setFocusVisible] = useState(undefined);
  const isSelectedDate = useCallback(day => {
    if (selectedDates && selectedDates.length > 0) {
      const startDate = selectedDates[0]?.startDate;
      const endDate = selectedDates[0]?.endDate;
      if (singleSelection && intervalSelection && startDate && day.getTime() >= startDate.getTime() && endDate && day.getTime() <= endDate.getTime()) {
        return true;
      }
      if (!singleSelection && !intervalSelection || displayedMonths.length > 1) {
        return selectedDates.some(selectedDate => selectedDate.startDate && isSameDay(selectedDate.startDate, day));
      }
    }
    if (dateRangeDates) {
      const startDate = dateRangeDates[0]?.startDate;
      const endDate = dateRangeDates[0]?.endDate;
      if (singleSelection && intervalSelection && startDate && day.getTime() >= startDate.getTime() && endDate && day.getTime() <= endDate.getTime()) {
        return true;
      }
      if (!singleSelection && !intervalSelection) {
        return dateRangeDates.some(selectedDate => selectedDate.startDate && isSameDay(selectedDate.startDate, day));
      }
    }
    return false;
  }, [dateRangeDates, displayedMonths.length, intervalSelection, selectedDates, singleSelection]);
  const hasIntervalDates = () => {
    if (selectedDates && selectedDates[0]?.startDate && selectedDates[0]?.endDate) return true;
    if (dateRangeDates && dateRangeDates[0]?.startDate && dateRangeDates[0]?.endDate) return true;
    return false;
  };
  const isDateWithinInterval = day => {
    if (selectedDates && selectedDates[0]?.startDate && selectedDates[0]?.endDate) {
      const startDate = selectedDates[0]?.startDate;
      const endDate = selectedDates[0]?.endDate;
      if (day.getTime() > startDate.getTime() && endDate && day.getTime() < endDate.getTime()) {
        return true;
      }
    }
    if (dateRangeDates) {
      const startDate = dateRangeDates[0]?.startDate;
      const endDate = dateRangeDates[0]?.endDate;
      if (startDate && endDate && day.getTime() > startDate.getTime() && endDate && day.getTime() < endDate.getTime()) {
        return true;
      }
    }
    return false;
  };
  const {
    ariaSelectedText,
    ariaSelectedDate,
    handleAriaDateSelection,
    resetAllAriaSelected
  } = useSelectedAriaDescribedby();
  const handleSelectionType = useCallback(value => {
    if (isDisabledDay(value)) return;
    const newDate = new Date(value);
    newDate.setHours(0, 0, 0, 0);
    const isSelected = isSelectedDate(newDate);
    const dateRange = [];
    setDateRangeDates([]);
    if (!intervalSelection) {
      handleAriaDateSelection(value, isSelected);
    }
    if (singleSelection) {
      if (intervalSelection) {
        if (selectedDates[0]?.startDate && !selectedDates[0]?.endDate) {
          if (newDate.getTime() < selectedDates[0].startDate.getTime()) {
            dateRange.push({
              startDate: newDate,
              endDate: selectedDates[0].startDate
            });
            setSelectedDates(dateRange);
          } else if (newDate.getTime() >= selectedDates[0].startDate.getTime()) {
            dateRange.push({
              startDate: selectedDates[0].startDate,
              endDate: newDate
            });
            setSelectedDates(dateRange);
          }
        } else {
          dateRange.push({
            startDate: newDate,
            endDate: undefined
          });
          setSelectedDates(dateRange);
        }
        onChange?.({
          value: new Date(value),
          dateRange
        });
      } else {
        selectedDates.length = 0;
        selectedDates.push({
          startDate: newDate,
          endDate: undefined
        });
        onChange?.({
          value: new Date(value)
        });
      }
    } else {
      // Check if date is currently selected and remove if so
      if (isSelected) {
        const index = selectedDates?.findIndex(selectedDate => selectedDate.startDate && isSameDay(selectedDate.startDate, newDate));
        if (index >= 0) {
          selectedDates?.splice(index, 1);
          onChange?.({
            value: null,
            dateRange: selectedDates
          });
        }
      } else {
        selectedDates.push({
          startDate: newDate,
          endDate: undefined
        });
        onChange?.({
          value: new Date(newDate),
          dateRange: selectedDates
        });
      }
    }
  }, [intervalSelection, isDisabledDay, isSelectedDate, onChange, selectedDates, singleSelection, handleAriaDateSelection]);
  const onDayCellPress = useCallback(value => {
    if (isDisabledDay(value)) return;
    dispatchFocusAction({
      type: 'selection',
      value
    });
    setState(new Date(value));
    handleSelectionType(value);
  }, [handleSelectionType, isDisabledDay]);
  const onTodayButtonPress = value => {
    setForceDayCellFocus(true);
    setHasDayCellFocus(true);
    dispatchFocusAction({
      type: 'selection',
      value
    });
    if (!isDisabledDay(value)) {
      setState(new Date(value));
      handleSelectionType(value);
    }
    props.onTodayButtonPress?.(value);
    // using setTimeout because reset depends on focus state which is updated asynchronously
    setTimeout(() => headerRef.current?.reset(), 0);
  };
  const dt = useDesignTokens();
  const tokens = getCalendarDesignTokens(dt, {
    showTodayButton: props.showTodayButton,
    showLegend: props.showLegend,
    formatedCalendar
  });
  const divRef = useRef(null);
  const selectionTextId = useCalendarSelectionId();
  const selectionStateTextId = useCalendarSelectionStateId();
  const invisibleTextStartId = useCalendarIntervalStartId();
  const invisibleTextEndId = useCalendarIntervalEndId();
  const invisibleSelectId = useCalendarSelectId();
  const getAriaDescribedBy = day => {
    const ariaDescribedBy = [];
    if (intervalSelection) {
      const startDate = selectedDates[0]?.startDate;
      const endDate = selectedDates[0]?.endDate;
      if (startDate && endDate && (isSameDay(day, startDate) || isSameDay(day, endDate))) {
        ariaDescribedBy.push(invisibleTextEndId);
      }
      if (startDate && !endDate && isSameDay(day, startDate)) {
        ariaDescribedBy.push(invisibleTextStartId);
      }
    }
    if (!intervalSelection && ariaSelectedDate && isSameDay(day, ariaSelectedDate)) {
      ariaDescribedBy.push(invisibleSelectId);
    }
    return ariaDescribedBy.join(' ');
  };
  const getSelectedAriaDescribedBy = () => {
    const ariaSelectedDescribedBy = [];
    if (selectedDates && selectedDates.length > 0) {
      ariaSelectedDescribedBy.push(selectionStateTextId);
    }
    ariaSelectedDescribedBy.push(selectionTextId);
    return ariaSelectedDescribedBy.join(' ');
  };
  const getSelectStateText = useCallback(() => {
    if (intervalSelection) {
      const startDate = selectedDates[0]?.startDate;
      const endDate = selectedDates[0]?.endDate;
      if (!startDate || !endDate) return '';
      return localize('interval.endDate.aria', {
        startDate: startDate.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }) ?? '',
        endDate: endDate.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }) ?? ''
      });
    } else if (!singleSelection) {
      const selDates = [];
      if (selectedDates && selectedDates.length > 0) {
        // Sort dates from lowest to highest
        const sortedArray = [...selectedDates];
        sortedArray.sort((a, b) => {
          const aTime = a.startDate?.getTime() ?? 0;
          const bTime = b.startDate?.getTime() ?? 0;
          return aTime < bTime ? -1 : aTime > bTime ? 1 : 0;
        });
        sortedArray.forEach(element => selDates.push(element.startDate?.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })));
        return localize('multipleSelectState.aria', {
          dates: selDates?.join(', ')
        });
      } else {
        return '';
      }
    } else {
      if (selectedDates && selectedDates.length > 0) {
        return localize('singleSelectState.aria', {
          date: selectedDates[0]?.startDate?.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }) ?? ''
        });
      } else {
        return '';
      }
    }
  }, [intervalSelection, localize, selectedDates, singleSelection]);

  // For multiple month calendar, get the first month date to render based on the number of months to display
  const determineFirstMonthDate = date => {
    const firstDate = new Date(date);
    firstDate.setDate(1);
    if (monthsCount <= 12) {
      // only if intervals fit into a year -> otherwise just display the months according to the date
      let month = date.getMonth();
      month = month - month % monthsCount;
      if (12 % monthsCount > 0 && month + monthsCount > 11) {
        // do not show months over year borders if possible
        month = 12 - monthsCount;
      }
      firstDate.setMonth(month);
    }
    return firstDate;
  };

  // For multiple month calendar, get the current month to render based on the monthCount from focusState
  const getMonthToRender = monthCount => {
    const dte = determineFirstMonthDate(focusState);
    const year = dte.getMonth() === 11 && monthCount > 0 ? dte.getFullYear() + 1 : dte.getFullYear();
    const date = new Date(year, dte.getMonth() === 11 && monthCount > 0 ? 0 : dte.getMonth() + monthCount, 1 // Set to the first day of the month
    );

    if (year < 100) {
      // JavaScript's Date object interprets years less than 100 as being in the 20th century.
      // For example, if year is 2, it will be interpreted as 1902.  This is not what we want.
      // We want it to be interpreted as 0002. So we need to set the year to the desired value.
      date.setFullYear(year);
    }
    return date;
  };
  useEffect(() => {
    if (singleSelection === false && props.dateRange) {
      // it's mulitple selection
      if (prevDateRangeDate && prevDateRangeDate.length > 0 && props.dateRange[0].startDate && prevDateRangeDate[0].startDate && !isSameDay(props.dateRange[0].startDate, prevDateRangeDate[0].startDate)) {
        // We have a new props.dateRange setting.  Set the new dateRangeDates
        // and clear out the previous selectedDates.
        setSelectedDates([]);
        setDateRangeDates(props.dateRange);
        setPrevDateRangeDate(props.dateRange);
      } else {
        if (selectedDates.length === 0) {
          const dates = [];
          props.dateRange.forEach(dateRange => {
            dates.push({
              startDate: dateRange.startDate
            });
            selectedDates.push({
              startDate: dateRange.startDate
            });
          });
          // force a re-render
          setSelectedDates(dates);
        }
      }
    }
  }, [prevDateRangeDate, props.dateRange, selectedDates, singleSelection]);
  useEffect(() => {
    if (singleSelection && intervalSelection && props.dateRange) {
      if (prevDateRangeDate && prevDateRangeDate.length > 0 && props.dateRange[0].startDate && prevDateRangeDate[0].startDate && props.dateRange[0].endDate && prevDateRangeDate[0].endDate && (!isSameDay(props.dateRange[0].startDate, prevDateRangeDate[0].startDate) || !isSameDay(props.dateRange[0].endDate, prevDateRangeDate[0].endDate))) {
        // We have a new props.dateRange setting.  Set the new dateRangeDates
        // and clear out the previous selectedDates.
        setSelectedDates([]);
        setDateRangeDates(props.dateRange);
        setPrevDateRangeDate(props.dateRange);
      } else {
        if (selectedDates.length === 0) {
          selectedDates.push({
            startDate: props.dateRange[0].startDate,
            endDate: props.dateRange[0].endDate
          });
        }
      }
    }
  }, [intervalSelection, prevDateRangeDate, props.dateRange, selectedDates, singleSelection]);
  useEffect(() => {
    if (props.value && !intervalSelection && selectedDates && selectedDates.length === 0) {
      setSelectedDates([{
        startDate: props.value,
        endDate: undefined
      }]);
    }
  }, [intervalSelection, props.value, selectedDates]);
  useEffect(() => {
    const months = [];
    monthsRendered.current.length = 0;
    for (let i = 0; i < monthsCount; i++) {
      const dte = getMonthToRender(i);
      const cal = getCalendarRows(dte.getMonth(), dte.getFullYear(), locale);
      monthsRendered.current.push(dte.getMonth());
      months.push(getFormatedCalendar(cal, dte));
    }
    setDisplayedMonths(months);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthsCount, focusState, locale, state]);

  // Check if a custom focus indicator is set. If so, we'll display the focus indicator
  // regardless of mouse/keyboard interaction
  const customFocusIndicator = useGetGlobalSetting(GlobalSettingsKeys.FOCUS_OVERRIDE) === 'true';

  // For multiple month calendar, get the current month aria-label
  const getMonthYearAriaForLocale = (date, localeTag) => {
    const format = new Intl.DateTimeFormat(localeTag, {
      month: 'long',
      year: 'numeric'
    });
    return format.format(date);
  };

  // For multiple month calendar, get the correct index for the day cell based on the month (gridIndex).
  // This index is used for the div ref in the dayCells array.
  const getDayIndex = (gridIndex, rowIndex, colIndex, rowsPerGrid) => {
    const daysPerGrid = rowsPerGrid * 7; // Assuming each grid has  5 or 6 rows and 7 columns (6 * 7 = 42)
    // Calculate the total number of days in previous grids
    const previousDays = gridIndex * daysPerGrid;
    // Calculate the current day index
    const currentIndex = rowIndex * 7 + colIndex;
    // Return the total index
    return previousDays + currentIndex;
  };
  const renderCalendar = (formattedCal, currMonth, index) => {
    const wkNumber = monthsCount === 1 ? weekNumber : getCalendarWeekNumber(currMonth.getFullYear(), currMonth.getMonth());
    return /*#__PURE__*/_jsxs("div", {
      ref: monthsCount === 1 ? divRef : e => {
        if (e) {
          if (!divRefs.includes(e)) {
            setRef(createSetDivRef(divRefs.length), e);
          }
        }
      },
      "data-testid": props.testId,
      className: cn.container,
      style: tokens,
      role: "group",
      "aria-label": props.ariaLabel ?? localize('calendar.aria'),
      "aria-labelledby": props.ariaLabelledBy,
      onKeyDown: e => {
        if (e.key === 'Enter') {
          // Marking this event is needed when DatePicker/Calendar is in a Dialog so that Enter doesn't trigger the Dialog's default button
          markEvent(e);
        }
        if (e.target.getAttribute('role') === 'gridcell' && (e.key === ' ' || e.key === 'Enter' || e.key === 'Spacebar')) {
          setDayCellPress(true);
        } else {
          setDayCellPress(false);
        }
      },
      onMouseDown: e => {
        if (e.button === 2) {
          // disable contextmenu on rmb
          e.preventDefault();
        }
        if (e.target.getAttribute('role') === 'gridcell') {
          setDayCellPress(true);
        } else {
          setDayCellPress(false);
        }
      },
      children: [intervalSelection && /*#__PURE__*/_jsxs("div", {
        style: {
          position: 'absolute'
        },
        children: [/*#__PURE__*/_jsx(InvisibleText, {
          id: invisibleTextStartId,
          testId: postfixTestId(props.testId, '-interval-start'),
          children: localize('interval.startDate.aria', {
            startDate: selectedDates[0]?.startDate?.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }) ?? ''
          })
        }), /*#__PURE__*/_jsx(InvisibleText, {
          id: invisibleTextEndId,
          testId: postfixTestId(props.testId, '-interval-end'),
          children: localize('interval.endDate.aria', {
            startDate: selectedDates[0]?.startDate?.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }) ?? '',
            endDate: selectedDates[0]?.endDate?.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }) ?? ''
          })
        })]
      }), /*#__PURE__*/_jsxs("div", {
        id: `month-${currMonth.getMonth()}-${currMonth.getFullYear()}`,
        role: "grid",
        ref: monthsCount === 1 ? refDayCellContainer : e => {
          setRef(createSetDayCellContainer(index), e);
        },
        className: cn.calendar,
        "aria-label": monthsCount === 1 ? localize('calendar.aria') : getMonthYearAriaForLocale(currMonth, locale),
        "aria-describedby": getSelectedAriaDescribedBy(),
        onFocusCapture: event => {
          // if more than one month is displayed, we need to check if the focus is on a different month and
          // going to that months day cell, if so, update the focus to that day cell
          if (monthsCount > 1 && event.relatedTarget && event.relatedTarget.nodeName !== 'INPUT' &&
          // if year field change don't dispatch focus action
          event.target.ariaLabel && event.target.ariaLabel !== focusState.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }) // if same day cell don't dispatch focus action
          ) {
            const newFocusState = new Date(event.target.ariaLabel);
            dispatchFocusAction({
              type: 'selection',
              value: newFocusState
            });
          }
          setFocusVisible(prev => prev === undefined || customFocusIndicator ? true : prev);
          setHasDayCellFocus(true);
        },
        onBlurCapture: event => {
          // only consider this a focus loss if the focus is moving outside of this div
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setFocusVisible(undefined);
            setHasDayCellFocus(false);
          }
        },
        onMouseDown: event => {
          setFocusVisible(customFocusIndicator ? true : false);
          if (monthsCount > 1) {
            scrollTo(event.currentTarget);
          }
        },
        onKeyDown: () => {
          setFocusVisible(true);
        },
        children: [index === 0 ? /*#__PURE__*/_jsx(InvisibleText, {
          id: selectionStateTextId,
          testId: postfixTestId(props.testId, '-selection-state'),
          children: getSelectStateText()
        }) : null, index === 0 ? /*#__PURE__*/_jsx(InvisibleText, {
          id: selectionTextId,
          children: localize(intervalSelection ? 'intervalSelect.msg' : !singleSelection ? 'multiSelect.msg' : 'singleSelect.msg')
        }) : null, !intervalSelection && ariaSelectedText ? /*#__PURE__*/_jsx(InvisibleText, {
          testId: postfixTestId(props.testId, '-selected-text-description'),
          id: invisibleSelectId,
          children: ariaSelectedText
        }) : null, /*#__PURE__*/_jsx("div", {
          role: "row",
          className: cx(cn.weekdayHeader, props.showWeekNumbers && cn.withWeekNumbers),
          children: /*#__PURE__*/_jsx(WeekdayHeaderCells, {
            dayOfWeekIds: dayOfWeekIds,
            showWeekNumbers: props.showWeekNumbers,
            month: monthsCount > 1 ? currMonth.getMonth() : undefined,
            firstDayOfWeek: firstDayOfWeek
          })
        }), formattedCal.map((row, rowIndex) => /*#__PURE__*/_jsxs("div", {
          role: "row",
          className: cx(cn.monthRow, props.showWeekNumbers && cn.withWeekNumbers),
          onKeyDown: event => {
            switch (event.key) {
              case 'ArrowDown':
                dispatchFocusAction({
                  type: 'step',
                  direction: 'down'
                });
                break;
              case 'ArrowUp':
                dispatchFocusAction({
                  type: 'step',
                  direction: 'up'
                });
                break;
              case 'ArrowLeft':
                dispatchFocusAction({
                  type: 'step',
                  direction: 'left'
                });
                break;
              case 'ArrowRight':
                dispatchFocusAction({
                  type: 'step',
                  direction: 'right'
                });
                break;
              case 'Home':
                dispatchFocusAction({
                  type: 'step',
                  direction: 'startOfWeek'
                });
                break;
              case 'End':
                dispatchFocusAction({
                  type: 'step',
                  direction: 'endOfWeek'
                });
                break;
              case 'PageUp':
                if (event.shiftKey && event.ctrlKey) dispatchFocusAction({
                  type: 'decadeStep',
                  direction: 'previous'
                });else if (event.shiftKey) dispatchFocusAction({
                  type: 'yearStep',
                  direction: 'previous'
                });else dispatchFocusAction({
                  type: 'monthStep',
                  direction: 'previous'
                });
                break;
              case 'PageDown':
                if (event.shiftKey && event.ctrlKey) dispatchFocusAction({
                  type: 'decadeStep',
                  direction: 'next'
                });else if (event.shiftKey) dispatchFocusAction({
                  type: 'yearStep',
                  direction: 'next'
                });else dispatchFocusAction({
                  type: 'monthStep',
                  direction: 'next'
                });
                break;
              case 'Enter': /* falls-through */
              case ' ':
                const newDate = new Date(focusState);
                if (isBeforeMinDate(newDate) || isAfterMaxDate(newDate)) return;
                setState(newDate);
                handleSelectionType(newDate);
                break;
              default:
            }
          },
          children: [props.showWeekNumbers && /*#__PURE__*/_jsx("div", {
            id: `${weekNumberIds[rowIndex]}`,
            role: "rowheader",
            "aria-label": localize('week.aria', {
              weekNumber: String(getWeekNum(wkNumber + rowIndex, currMonth.getMonth(), currMonth.getFullYear()))
            }),
            className: cn.weekNumberCell,
            children: getWeekNum(wkNumber + rowIndex, currMonth.getMonth(), currMonth.getFullYear())
          }), row.map((day, colIndex) => /*#__PURE__*/_jsx(DayCell, {
            testId: `calendarDay(${day.getMonth()}/${day.getDate()}/${day.getFullYear()})`,
            ref: e => {
              if (monthsCount === 1) {
                setRef(createSetDayCell(rowIndex * 7 + colIndex), e);
              } else {
                if (e) {
                  setRef(createSetDayCell(getDayIndex(index, rowIndex, colIndex, displayedMonths[index].length)), e);
                }
              }
            },
            day: day,
            today: monthsCount === 1 ? isCurrentMonth(day, rowIndex) && isSameDay(day, today) : displayedMonths.length > 1 && !isSelectedDate(day) || !isCurrentMonth(day, rowIndex) ? false : isSameDay(day, today),
            selected: displayedMonths.length > 1 && isSelectedDate(day) && isCurrentMonth(day, rowIndex) ? true : displayedMonths.length > 1 && !isSelectedDate(day) || !isCurrentMonth(day, rowIndex) ? false : singleSelection && intervalSelection && hasIntervalDates() || !singleSelection && selectedDates.length > 0 ? isSelectedDate(day) : props.value ? singleSelection && (isSameDay(day, props.value) || isSameDay(day, firstValidDate)) : false,
            inInterval: singleSelection && intervalSelection && hasIntervalDates() ? isDateWithinInterval(day) : false,
            focused: monthsCount === 1 ? isSameDay(day, focusState) : isCurrentMonth(day, rowIndex) && isSameDay(day, focusState) || monthsCount > 1 &&
            // if the month is not the current month, but the day is the same as the focus state
            isCurrentMonth(day, rowIndex) && day.getDate() === focusState.getDate()
            // NOTE: DayCell is memoed, so it's best to add the isSameDay check
            ,
            focusVisible: monthsCount === 1 ? isSameDay(day, focusState) && !!focusVisible : isCurrentMonth(day, rowIndex) && isSameDay(day, focusState) && !!focusVisible,
            disabled: isDisabledDay(day),
            inCurrentMonth: isArSaLocale(locale) ? isCurrentMonth(day, rowIndex) : day.getMonth() === currMonth.getMonth()
            // FIXME: use formatter
            ,
            ariaLabel: day.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }),
            ariaDescribedBy: getAriaDescribedBy(day),
            onPress: onDayCellPress,
            onBlur: () => {
              // clear out text to prevent being read twice
              resetAllAriaSelected();
            },
            nonWorkDay: isNonWorkDay(day),
            specialDay: isSpecialDay(day),
            nonWorkDayAriaLabel: props.nonWorkDayLegendText,
            specialDayAriaLabel: props.specialDates?.legendText,
            intervalSelection: intervalSelection,
            singleSelection: singleSelection,
            children: isArSaLocale(locale) ? isCurrentMonth(day, rowIndex) ? dayFormat(day) : '' : day.getMonth() === currMonth.getMonth() ? day.getDate() : ''
          }, monthsCount === 1 ? colIndex : `${index}-${day.getDate()}`))]
        }, rowIndex))]
      }, currMonth.getMonth()), props.showTodayButton ? /*#__PURE__*/_jsx("div", {
        className: cn.todayButtonFooter,
        children: /*#__PURE__*/_jsx(Button, {
          variant: ButtonVariant.Small,
          text: localize('today.label'),
          onPress: () => {
            onTodayButtonPress(today);
          },
          ariaLabel: localize('today.label'),
          testId: postfixTestId(props.testId, 'todayButton'),
          className: cn.todayButton
        })
      }) : null, index === 0 ? renderMonthHeader() : index === 1 && displayedMonths.length === 2 ? renderSecondMonthHeader() : index >= 0 && displayedMonths.length > 1 && displayedMonths.length !== 2 && renderMultipleMonthHeader('multipeMonthHead', getMonthToRender(index)), index < displayedMonths.length - 1 && /*#__PURE__*/_jsx("hr", {
        className: cn.calDivider
      }), ' ', props.showLegend ? /*#__PURE__*/_jsx(CalendarLegend, {
        specialDayLegendText: props.specialDates?.legendText,
        nonWorkDayLegendText: props.nonWorkDayLegendText,
        legendItems: props.legendItems,
        testId: postfixTestId(props.testId, 'legend')
      }) : null]
    });
  };
  const renderMonthHeader = () => {
    return /*#__PURE__*/_jsx(MonthCalendarHeader, {
      ref: element => {
        setRef(ref, element);
        setRef(headerRef, element);
      },
      month: focusState.getMonth(),
      focusDate: focusState.getDate(),
      formatedMonth: parseInt(monthFormat(displayedMonths.length > 1 ? determineFirstMonthDate(focusState) : focusState)) - 1,
      year: isThaiLocale(locale) && focusState.getFullYear() > 999 // Thai year should be at least 4 digits
      ? convertGregorianYearToThaiYear(focusState.getFullYear()) : focusState.getFullYear(),
      onChange: value => dispatchFocusAction({
        type: 'monthSelect',
        value
      }),
      onPreviousButtonPress: value => props.onPreviousButtonPress?.(value),
      onNextButtonPress: value => props.onNextButtonPress?.(value),
      onMonthSelect: value => props.onMonthSelect?.(value),
      onYearChange: value => props.onYearChange?.(value),
      selectClassName: props.selectClassName,
      numberMonthsDisplayed: displayedMonths.length,
      displayedMonths: monthsRendered.current,
      minDate: minDate,
      maxDate: maxDate,
      dayCellPress: dayCellPress
    });
  };
  const renderSecondMonthHeader = () => {
    const nextMonth = new Date(displayedMonths.length > 1 ? determineFirstMonthDate(focusState) : focusState);
    nextMonth.setDate(1); // Set to the first day of the month
    if (nextMonth.getMonth() === 11) {
      nextMonth.setFullYear(nextMonth.getFullYear() + 1);
      nextMonth.setMonth(0);
    } else {
      nextMonth.setMonth(nextMonth.getMonth() + 1);
    }
    if (nextMonth.getMonth() === focusState.getMonth()) {
      nextMonth.setDate(focusState.getDate());
    }
    return /*#__PURE__*/_jsx(MonthCalendarHeader, {
      ref: element => {
        setRef(ref, element);
        setRef(headerRef, element);
      },
      month: nextMonth.getMonth(),
      focusDate: nextMonth.getDate(),
      formatedMonth: parseInt(monthFormat(nextMonth)) - 1,
      year: isThaiLocale(locale) && nextMonth.getFullYear() > 999 // Thai year should be at least 4 digits
      ? convertGregorianYearToThaiYear(nextMonth.getFullYear()) : nextMonth.getFullYear(),
      onChange: value => dispatchFocusAction({
        type: 'monthSelect',
        value
      }),
      onMonthSelect: value => props.onMonthSelect?.(value),
      onYearChange: value => props.onYearChange?.(value),
      selectClassName: props.selectClassName,
      showNextButton: false,
      showPreviousButton: false,
      numberMonthsDisplayed: displayedMonths.length,
      displayedMonths: monthsRendered.current,
      minDate: minDate,
      maxDate: maxDate,
      dayCellPress: dayCellPress
    });
  };
  const renderMultipleMonthHeader = (testId, currMonth) => {
    return /*#__PURE__*/_jsx(MultipleMonthCalendarHeader, {
      testId: postfixTestId(testId, currMonth.getMonth().toString()),
      month: currMonth
    });
  };
  return /*#__PURE__*/_jsx("div", {
    className: cn.container,
    children: monthsCount === 1 ? renderCalendar(formatedCalendar, focusState, 0) : Array.from({
      length: displayedMonths.length
    }, (_, index) => /*#__PURE__*/_jsx(Fragment, {
      children: renderCalendar(displayedMonths[index], getMonthToRender(index), index)
    }, index))
  });
});
//# sourceMappingURL=BaseCalendar.js.map